import * as THREE from 'three'
import { TimelineLite, TweenLite } from 'gsap'

import Terrain from '../Terrain.js'
import ScanModel from '../ScanModel.js'
import DroningInteraction from '../Interactions/Droning.js'

export default class Investigate
{
    constructor(_options)
    {
        this.$eventHub = _options.$eventHub
        this.camera = _options.camera
        this.loader = _options.loader
        this.time = _options.time
        this.sizes = _options.sizes
        this.ui = _options.ui
        this.mobile = _options.mobile
        this.cursor = _options.cursor
        this.position = _options.position
        this.terrainGeometry = _options.terrainGeometry

        this.name = 'investigate'
        this.container = new THREE.Object3D()
        this.container.position.copy(this.position)

        this.setTerrain()
    }

    /**
     * Set terrain
     */
    setTerrain()
    {
        this.terrain = new Terrain({
            mobile: this.mobile,
            time: this.time,
            loader: this.loader,
            ui: this.ui,
            geometry: this.terrainGeometry,
            texture: this.loader.resources.terrainTexture
        })

        this.terrain.plane.material.uniforms.uElevationNoiseMultiplier.value = 0.07
        this.terrain.plane.material.uniforms.uAlpha.value = 0

        this.container.add(this.terrain.container)
    }

    /**
     * Set contents
     */
    setContents()
    {
        this.setModels()
        this.setInteraction()
        this.setSliderTimeline()
        this.setFocusTimeline()
    }

    /**
     * Set models
     */
    setModels()
    {
        /**
         * Drone
         */
        this.drone = {}
        this.drone.container = new THREE.Object3D()
        this.drone.container.scale.x = 0.05
        this.drone.container.scale.y = 0.05
        this.drone.container.scale.z = 0.05
        this.drone.container.position.x = 0
        this.drone.container.position.z = - 5
        this.drone.container.position.y = 2
        this.drone.container.rotation.y = - Math.PI * 0.5
        this.container.add(this.drone.container)

        this.drone.swaying = new THREE.Object3D()
        this.drone.container.add(this.drone.swaying)

        this.drone.base = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.droneBaseModelObj.geometry,
            revealPosition: { x: 7, y: - 1, z: 0 },
            color: { x: 1, y: 0.9, z: 0 } // Yellow
        })
        this.drone.swaying.add(this.drone.base.container)

        this.drone.rotor = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.droneRotorModelObj.geometry,
            revealPosition: { x: 7, y: - 1, z: 0 },
            color: { x: 1, y: 0.9, z: 0 } // Yellow
        })
        this.drone.swaying.add(this.drone.rotor.container)

        this.drone.backRotor = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.droneBackRotorModelObj.geometry,
            revealPosition: { x: 7, y: - 1, z: 0 },
            color: { x: 1, y: 0.9, z: 0 } // Yellow
        })
        this.drone.backRotor.container.position.x = - 9.28
        this.drone.backRotor.container.position.z = - 0.4
        this.drone.swaying.add(this.drone.backRotor.container)

        this.time.on('tick', () =>
        {
            this.drone.rotor.container.rotation.y = this.time.elapsed * 0.015
            this.drone.backRotor.container.rotation.z = this.time.elapsed * 0.02
            this.drone.swaying.rotation.x = Math.sin(this.time.elapsed * 0.0002) * 0.02
            this.drone.swaying.rotation.z = Math.sin(this.time.elapsed * 0.000678) * 0.005
            this.drone.swaying.position.x = Math.sin(this.time.elapsed * 0.00023) * 0.2
            this.drone.swaying.position.z = Math.sin(this.time.elapsed * 0.000778) * 0.5
            this.drone.swaying.position.y = Math.sin(this.time.elapsed * 0.000578) * 0.3
        })

        /**
         * Topography
         */
        this.topography = {}
        this.topography.container = new THREE.Object3D()
        this.topography.activeLayerIndex = 4
        this.topography.size = 10
        this.topography.progress = 0
        this.container.add(this.topography.container)

        this.topography.block1 = {}
        this.topography.block1.layers = []
        this.topography.block1.container = new THREE.Object3D()
        this.topography.container.add(this.topography.block1.container)

        this.topography.block2 = {}
        this.topography.block2.layers = []
        this.topography.block2.container = new THREE.Object3D()
        this.topography.block2.container.position.z = this.topography.size
        this.topography.container.add(this.topography.block2.container)

        for(let i = 0; i < 6; i++)
        {
            for(let j = 0; j < 2; j++)
            {
                const block = this.topography[`block${j + 1}`]

                const color = i === this.topography.activeLayerIndex ? { x: 1, y: 0.9, z: 0 } : { x: 0.18, y: 0.18, z: 0.18 }
                const layer = new ScanModel({
                    camera: this.camera,
                    sizes: this.sizes,
                    time: this.time,
                    geometry: this.loader.resources[`topography${i + 1}ModelObj`].geometry,
                    revealPosition: { x: -57.66, y: 1.24, z: 18.7 },
                    color: color
                })
                layer.container.scale.x = 6
                layer.container.scale.y = 6
                layer.container.scale.z = 6
                layer.container.position.x = 0
                layer.container.position.y = i * 0.1
                layer.container.position.z = 0

                block.container.add(layer.container)
                block.layers.push(layer)
            }
        }

        this.time.on('tick', () =>
        {
            const progress = this.time.elapsed * 0.00002 % 1

            // Reset
            if(progress < this.topography.progress)
            {
                let i = 0
                for(const _layer of this.topography.block1.layers)
                {
                    const color = i === this.topography.activeLayerIndex ? { x: 1, y: 0.9, z: 0 } : { x: 0.18, y: 0.18, z: 0.18 }
                    _layer.material.uniforms.uColor.value.set(color.x, color.y, color.z)

                    i++
                }

                this.topography.activeLayerIndex = Math.floor(Math.random() * 6)

                // Fade in
                i = 0
                for(const _layer of this.topography.block2.layers)
                {
                    TweenLite.fromTo(_layer.material.uniforms.uAlpha, 4, { value: 0 }, { value: 1 })

                    const color = i === this.topography.activeLayerIndex ? { x: 1, y: 0.9, z: 0 } : { x: 0.18, y: 0.18, z: 0.18 }
                    _layer.material.uniforms.uColor.value.set(color.x, color.y, color.z)

                    i++
                }
            }

            this.topography.container.position.z = - progress * this.topography.size
            this.topography.progress = progress
        })

        // // UI
        // if(this.ui)
        // {
        //     // Folder
        //     const folder = this.ui.addFolder('drone base')
        //     folder.open()

        //     // Dummy
        //     const dummy = {}

        //     // Tweaks
        //     folder
        //         .add(this.drone.rotor.material.uniforms.uRevealPosition.value, 'x', - 100, 100, 0.01)
        //         .name('uRevealPosition x')

        //     folder
        //         .add(this.drone.rotor.material.uniforms.uRevealPosition.value, 'y', - 100, 100, 0.01)
        //         .name('uRevealPosition y')

        //     folder
        //         .add(this.drone.rotor.material.uniforms.uRevealPosition.value, 'z', - 100, 100, 0.01)
        //         .name('uRevealPosition z')

        //     folder
        //         .add(this.drone.rotor.material.uniforms.uRevealDistance, 'value', 0, 100, 0.01)
        //         .name('uRevealDistance')
        // }
    }

    /**
     * Set interaction
     */
    setInteraction()
    {
        this.interaction = new DroningInteraction({
            $eventHub: this.$eventHub,
            mobile: this.mobile,
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            cursor: this.cursor,
            ui: this.ui,
            loader: this.loader
        })
        this.interaction.container.position.x = 1.03
        this.interaction.container.position.z = - 4.34
        this.interaction.container.position.y = - 45.5

        this.interaction.container.rotation.order = 'YXZ'
        this.interaction.container.rotation.x = 0.00
        this.interaction.container.rotation.y = 0.52
        this.interaction.container.rotation.z = 0
    }

    /**
     * Show
     */
    show()
    {
        this.sliderTimeline.play()
    }

    /**
     * Hide
     */
    hide()
    {
        this.sliderTimeline.reverse()
    }

    /**
     * Set timeline
     */
    setSliderTimeline()
    {
        this.sliderTimeline = new TimelineLite({ paused: true })

        this.sliderTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uAlpha, 2, { value: 0 }, { value: 1 }), 0)

        for(let i = 0; i < this.topography.block1.layers.length; i++)
        {
            const _layer1 = this.topography.block1.layers[i]
            const _layer2 = this.topography.block2.layers[i]
            this.sliderTimeline.add(TweenLite.fromTo(_layer1.material.uniforms.uAlpha, 1, { value: 0 }, { value: 1 }), 2 + i * 0.35)
            this.sliderTimeline.add(TweenLite.fromTo(_layer2.material.uniforms.uAlpha, 1, { value: 0 }, { value: 1 }), 2 + i * 0.35)
        }

        this.sliderTimeline.add(TweenLite.fromTo(this.drone.base.material.uniforms.uRevealDistance, 1.2, { value: 17.5 }, { value: 2 }), 2.5)
        this.sliderTimeline.add(TweenLite.fromTo(this.drone.rotor.material.uniforms.uRevealDistance, 1.2, { value: 17.5 }, { value: 2 }), 3)
        this.sliderTimeline.add(TweenLite.fromTo(this.drone.backRotor.material.uniforms.uRevealDistance, 1.2, { value: 17.5 }, { value: 2 }), 3)
    }

    /**
     * Set focus timeline
     */
    setFocusTimeline()
    {
        this.focusTimeline = new TimelineLite({ paused: true })

        this.focusTimeline.call(() =>
        {
            // Entering
            if(!this.focusTimeline._reversed)
            {
                this.container.add(this.interaction.container)
            }
            // Leaving
            else
            {
                this.container.remove(this.interaction.container)
            }
        }, null, this, 0)

        // Terrain
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uColor.value, 2, { x: 0.12, y: 0.12, z: 0.12 }, { x: 0.06, y: 0.06, z: 0.06 }), 0)

        // Tank
        this.focusTimeline.call(() =>
        {
            if(!this.focusTimeline._reversed)
            {
                this.interaction.reset()
            }
        }, null, this, 0)
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/Experience/Scenes/Investigate.js